import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns true if the given string is a valid GUID/UUID.
 */
export function isGuid(value: string): boolean {
  return GUID_REGEX.test(value);
}

/**
 * Returns the trailing segment of a `/`-joined graph node path (e.g. a debugger
 * state key or node path), or the whole string if it contains no `/`. Used
 * instead of `endsWith` to avoid matching a segment that's merely a substring
 * of a longer one (e.g. "Income" inside "Total Income").
 */
export function lastPathSegment(path: string): string {
  const segments = path.split("/");
  return segments[segments.length - 1];
}

/**
 * A fixed-size circular (ring) buffer.
 *
 * Stores up to `capacity` items. When full, adding a new item overwrites
 * the oldest one. All operations are O(1).
 */
export class CircularBuffer<T> implements Iterable<T> {
  private buffer: (T | undefined)[]
  private start = 0
  private size = 0

  /**
   * @param capacity Maximum number of items the buffer can hold.
   * @throws Error if capacity is less than 1
   */
  constructor(private readonly capacity: number) {
    if (capacity < 1) {
      throw new Error('CircularBuffer capacity must be at least 1')
    }

    this.buffer = new Array<T | undefined>(capacity)
  }

  /**
   * Add an item to the buffer.
   *
   * If the buffer is full, the oldest item is overwritten.
   */
  push(value: T): void {
    const index = (this.start + this.size) % this.capacity

    if (this.size < this.capacity) {
      this.buffer[index] = value
      this.size++
    } else {
      this.buffer[this.start] = value
      this.start = (this.start + 1) % this.capacity
    }
  }

  /**
   * Return all items in logical order (oldest → newest).
   */
  toArray(): T[] {
    return [...this]
  }

  /** Current number of items in the buffer */
  get length(): number {
    return this.size
  }

  /** Whether the buffer is empty */
  get isEmpty(): boolean {
    return this.size === 0
  }

  /** Whether the buffer is full */
  get isFull(): boolean {
    return this.size === this.capacity
  }

  /** Remove all items from the buffer */
  clear(): void {
    this.start = 0
    this.size = 0
    this.buffer.fill(undefined)
  }

  /**
   * Iterate over items in logical order (oldest → newest).
   */
  *[Symbol.iterator](): Iterator<T> {
    for (let i = 0; i < this.size; i++) {
      const value = this.buffer[(this.start + i) % this.capacity]
      if (value !== undefined) {
        yield value
      }
    }
  }
}

